import { ref, type ComputedRef } from 'vue';
import { useModal } from 'vue-final-modal';
import { ConfirmDeleteModal } from '@/shared/ui';
import { useCustomToast } from '@/shared/lib';
import SettingsFieldModal from '../ui/SettingsFieldModal.vue';
import RecordOriginModal from '../ui/RecordOriginModal.vue';
import { SETTINGS_DELETE_DESCRIPTION, SETTINGS_DELETE_TITLES, SETTINGS_TOAST_MESSAGES } from './settings-messages';
import type { SettingsNameConflict } from './settings-name-conflict';
import type {
  RecordOriginItem,
  RecordOriginLink,
  RecordOriginPayload,
  SettingsListItem,
  SettingsSectionConfig,
} from '../model/settings.types';

function useDeleteConfirmationModal(title: string, remove: (id: number) => Promise<boolean>) {
  const deletingId = ref<number | null>(null);

  const deleteModal = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      title,
      description: SETTINGS_DELETE_DESCRIPTION,
      onClose: () => {
        deleteModal.close();
        deletingId.value = null;
      },
      onConfirm: async () => {
        if (deletingId.value === null) return;

        await remove(deletingId.value);
        deleteModal.close();
        deletingId.value = null;
      },
    },
  });

  return {
    openDelete: (id: number) => {
      deletingId.value = id;
      deleteModal.open();
    },
  };
}

export interface SettingsFieldManagement {
  items: ComputedRef<readonly SettingsListItem[]>;
  nameConflict: ComputedRef<SettingsNameConflict | null>;
  validationMessages: ComputedRef<readonly string[]>;
  add(names: string[]): Promise<boolean>;
  edit(id: number, name: string): Promise<boolean>;
  remove(id: number): Promise<boolean>;
}

export function useSettingsFieldModals(
  section: SettingsSectionConfig,
  management: SettingsFieldManagement
) {
  const findItem = (id: number) => management.items.value.find((item) => item.id === id) ?? null;

  const editingId = ref<number | null>(null);
  const isSaving = ref(false);
  const duplicateIndices = ref<readonly number[]>([]);
  const toast = useCustomToast();
  const onDuplicate = () =>
    toast.showErrorToast({ title: SETTINGS_TOAST_MESSAGES.duplicateError, type: 'error' });

  const validationMessages = ref<readonly string[]>([]);

  const reportRejection = () => {
    duplicateIndices.value = management.nameConflict.value?.conflictingIndexes ?? [];
    validationMessages.value = management.validationMessages.value;
  };

  const clearRejection = () => {
    duplicateIndices.value = [];
    validationMessages.value = [];
  };

  const addModal = useModal({
    component: SettingsFieldModal,
    attrs: {
      mode: 'add' as const,
      title: `Adicionar ${section.title}`,
      fieldLabel: section.fieldLabel,
      fieldPlaceholder: section.fieldPlaceholder,
      get isSaving() {
        return isSaving.value;
      },
      get duplicateIndices() {
        return duplicateIndices.value;
      },
      get validationMessages() {
        return validationMessages.value;
      },
      onClose: () => addModal.close(),
      onDuplicate,
      onSave: async (values: string[]) => {
        if (isSaving.value) return;

        isSaving.value = true;
        clearRejection();
        try {
          const success = await management.add(values);
          if (success) addModal.close();
          else reportRejection();
        } finally {
          isSaving.value = false;
        }
      },
    },
  });

  const editModal = useModal({
    component: SettingsFieldModal,
    attrs: {
      mode: 'edit' as const,
      get title() {
        return `Editar ${section.title}`;
      },
      fieldLabel: section.fieldLabel,
      fieldPlaceholder: section.fieldPlaceholder,
      get initialValue() {
        return editingId.value !== null ? (findItem(editingId.value)?.name ?? '') : '';
      },
      get isSaving() {
        return isSaving.value;
      },
      get duplicateIndices() {
        return duplicateIndices.value;
      },
      get validationMessages() {
        return validationMessages.value;
      },
      // No onDuplicate here: the edition has a single field, so the modal has no second name of
      // the same request to repeat, and it never emits the event.
      onClose: () => {
        editModal.close();
        editingId.value = null;
      },
      onSave: async (values: string[]) => {
        if (editingId.value === null || isSaving.value) return;

        isSaving.value = true;
        clearRejection();
        try {
          const success = await management.edit(editingId.value, values[0] ?? '');
          if (success) {
            editModal.close();
            editingId.value = null;
          } else {
            reportRejection();
          }
        } finally {
          isSaving.value = false;
        }
      },
    },
  });

  const { openDelete } = useDeleteConfirmationModal(SETTINGS_DELETE_TITLES[section.key], management.remove);

  return {
    openAdd: () => {
      clearRejection();
      addModal.open();
    },
    openEdit: (id: number) => {
      editingId.value = id;
      clearRejection();
      editModal.open();
    },
    openDelete,
  };
}

export interface RecordOriginManagement {
  items: ComputedRef<readonly RecordOriginItem[]>;
  nameConflict: ComputedRef<SettingsNameConflict | null>;
  validationMessages: ComputedRef<readonly string[]>;
  add(payload: RecordOriginPayload): Promise<boolean>;
  edit(id: number, payload: RecordOriginPayload): Promise<boolean>;
  remove(id: number): Promise<boolean>;
}

const linkIds = (links?: readonly RecordOriginLink[]): number[] =>
  (links ?? []).map((link) => link.id);

export function useRecordOriginModals(
  management: RecordOriginManagement,
  recordTypeOptions: ComputedRef<readonly SettingsListItem[]>,
  protectionMeanOptions: ComputedRef<readonly SettingsListItem[]>
) {
  const findItem = (id: number | null) =>
    id === null ? null : (management.items.value.find((item) => item.id === id) ?? null);

  function sanitizePayload(payload: RecordOriginPayload): RecordOriginPayload {
    const validRecordTypeIds = new Set(recordTypeOptions.value.map((option) => option.id));
    const validProtectionMeanIds = new Set(protectionMeanOptions.value.map((option) => option.id));

    return {
      name: payload.name,
      recordTypeIds: payload.recordTypeIds.filter((id) => validRecordTypeIds.has(id)),
      protectionMeanIds: payload.protectionMeanIds.filter((id) => validProtectionMeanIds.has(id)),
    };
  }

  const editingId = ref<number | null>(null);
  const isSaving = ref(false);
  const hasNameConflict = ref(false);
  const validationMessages = ref<readonly string[]>([]);

  /**
   * The server decides what is the same name, and there is no local pre-check to hold a second
   * opinion. One origin travels per request, so the conflict it reports is always position zero and
   * what the modal needs from it is only whether to highlight the name field.
   *
   * The toast is not raised here: the refused write already raised one, carrying the name the
   * server refused. Raising the generic copy on top of it shows two toasts for one refusal.
   */
  const reportRejection = () => {
    hasNameConflict.value = management.nameConflict.value !== null;
    validationMessages.value = management.validationMessages.value;
  };

  const clearRejection = () => {
    hasNameConflict.value = false;
    validationMessages.value = [];
  };

  const addModal = useModal({
    component: RecordOriginModal,
    attrs: {
      mode: 'add' as const,
      title: 'Adicionar Origem do Registro',
      get recordTypeOptions() {
        return recordTypeOptions.value;
      },
      get protectionMeanOptions() {
        return protectionMeanOptions.value;
      },
      get isSaving() {
        return isSaving.value;
      },
      get hasNameConflict() {
        return hasNameConflict.value;
      },
      get validationMessages() {
        return validationMessages.value;
      },
      onClose: () => addModal.close(),
      onSave: async (payload: RecordOriginPayload) => {
        if (isSaving.value) return;

        isSaving.value = true;
        clearRejection();
        try {
          const success = await management.add(sanitizePayload(payload));
          if (success) addModal.close();
          else reportRejection();
        } finally {
          isSaving.value = false;
        }
      },
    },
  });

  const editModal = useModal({
    component: RecordOriginModal,
    attrs: {
      mode: 'edit' as const,
      title: 'Editar Origem do Registro',
      get initialName() {
        return findItem(editingId.value)?.name ?? '';
      },
      /**
       * Only the links the listing still resolves. A link whose item was deleted logically stays in
       * the base and never reaches the screen, so the modal opens without it and the save drops it.
       */
      get initialRecordTypeIds() {
        return linkIds(findItem(editingId.value)?.recordTypes);
      },
      get initialProtectionMeanIds() {
        return linkIds(findItem(editingId.value)?.protectionMeans);
      },
      get recordTypeOptions() {
        return recordTypeOptions.value;
      },
      get protectionMeanOptions() {
        return protectionMeanOptions.value;
      },
      get isSaving() {
        return isSaving.value;
      },
      get hasNameConflict() {
        return hasNameConflict.value;
      },
      get validationMessages() {
        return validationMessages.value;
      },
      onClose: () => {
        editModal.close();
        editingId.value = null;
      },
      onSave: async (payload: RecordOriginPayload) => {
        if (editingId.value === null || isSaving.value) return;

        isSaving.value = true;
        clearRejection();
        try {
          const success = await management.edit(editingId.value, sanitizePayload(payload));
          if (success) {
            editModal.close();
            editingId.value = null;
          } else {
            reportRejection();
          }
        } finally {
          isSaving.value = false;
        }
      },
    },
  });

  const { openDelete } = useDeleteConfirmationModal(SETTINGS_DELETE_TITLES.recordOrigin, management.remove);

  return {
    openAdd: () => {
      clearRejection();
      addModal.open();
    },
    openEdit: (id: number) => {
      editingId.value = id;
      clearRejection();
      editModal.open();
    },
    openDelete,
  };
}
